/**
 * POC Page - Three.js 技术可行性验证
 *
 * 验证目标：
 * 1. Three.js 加载和渲染 3D 模型
 * 2. 实时颜色切换
 * 3. 配件显示/隐藏
 * 4. 声音引擎集成
 * 5. 亚太区 CDN 兼容性
 */

import { useState, useRef, useEffect, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF, Html, Center, ContactShadows, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import styles from './POCPage.module.css';
import engineSoundUrl from '../../assets/Sportster 2017 OEM, klep open, mic combi.mp3';
import { getModelUrl, getTextureUrl, isUsingCDN, getR2BaseUrl } from '../../config/assets';
import { CATEGORIES, PARTS, BASE_PRICE, isMultiSelect } from '../../data/parts';
import ConfigLibraryModal from '../../components/legend/ConfigLibraryModal';
import SaveSuccessModal from '../../components/config/SaveSuccessModal';
import * as api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

// R2 CDN URLs（生产环境必须配置 VITE_R2_BASE_URL）
const R2_BASE = getR2BaseUrl();
const R2_MODEL_URL = R2_BASE ? `${R2_BASE}/models/harley_davidson_xr1200x.glb` : null;
const R2_FLAME_URL = R2_BASE ? `${R2_BASE}/textures/flame_pattern.png` : null;
const R2_HORSE_URL = R2_BASE ? `${R2_BASE}/textures/horse_pattern.png` : null;

// 根据环境选择资源来源
// 生产环境使用 R2 CDN，开发环境动态导入本地文件
const flamePatternUrl = R2_FLAME_URL || '/assets/3D/textures/flame_pattern.png';
const horsePatternUrl = R2_HORSE_URL || '/assets/3D/textures/horse_pattern.png';



// ============================================
// 声音引擎 Hook
// ============================================
// ============================================
// 声音引擎 Hook
// ============================================
function useSoundEngine() {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [rpm, setRpm] = useState(800); // 怠速 RPM

    useEffect(() => {
        // 初始化音频对象
        const audio = new Audio(engineSoundUrl);
        audio.loop = false; // 只需要播放一次
        audio.onended = () => setIsPlaying(false);
        audioRef.current = audio;

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const startEngine = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.error("Audio play failed:", e));
            setIsPlaying(true);
        }
    };

    const stopEngine = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setRpm(800);
    };

    const revEngine = (targetRpm) => {
        setRpm(targetRpm);
        // 简单的 RPM 模拟：改变播放速度
        if (audioRef.current && isPlaying) {
            // 假设 800 RPM 是 1.0 速度，6000 RPM 是 1.5 速度
            const rate = 1.0 + ((targetRpm - 800) / 5200) * 0.5;
            audioRef.current.playbackRate = Math.max(0.5, Math.min(rate, 2.0));
        }
    };

    return { isPlaying, rpm, startEngine, stopEngine, revEngine };
}

// ============================================
// 摩托车 3D 模型组件（GLB版）
// ============================================
// 模型 URL：生产环境使用 R2 CDN，开发环境使用本地文件
const modelUrl = R2_MODEL_URL || '/assets/3D/harley_davidson_xr1200x.glb';

function MotorcycleModel({ color, hiddenNodes, debugScale, debugY, onPaintableMeshes, customTexture, selectedPaintParts = [], customColorHex = '#FF6B00', textureBrightness = 1 }) {
    const groupRef = useRef();
    const { scene: originalScene } = useGLTF(modelUrl);
    const scene = useMemo(() => originalScene.clone(), [originalScene]);
    const allMeshesRef = useRef({});
    const paintableMeshesRef = useRef([]); // 可喷漆的mesh列表
    const originalMaterialsRef = useRef({}); // 原始材质备份

    // 加载纹理
    const flameTexture = useTexture(flamePatternUrl);
    const horseTexture = useTexture(horsePatternUrl);

    // 配置纹理属性
    useEffect(() => {
        if (flameTexture) {
            flameTexture.wrapS = THREE.RepeatWrapping;
            flameTexture.wrapT = THREE.RepeatWrapping;
            // 调整纹理重复次数和偏移，使其在车身上看起来更自然
            // 这通常需要根据具体的UV映射来调整
            flameTexture.repeat.set(2, 2);
            flameTexture.rotation = Math.PI / 2;
        }
        if (horseTexture) {
            horseTexture.wrapS = THREE.RepeatWrapping;
            horseTexture.wrapT = THREE.RepeatWrapping;
            horseTexture.repeat.set(1, 1); // 马年图案可能需要不同的重复设置
            horseTexture.rotation = Math.PI / 2;
        }
    }, [flameTexture, horseTexture]);

    // PBR 汽车漆材质配置 - 2026 FAT BOY® 哈雷官方配色
    // 参数说明: color, metalness, roughness, clearcoat, clearcoatRoughness
    const carPaintConfig = {
        'dark-billiard-gray': {
            color: new THREE.Color(0x6B6B6B),      // 中深灰
            metalness: 0.25,
            roughness: 0.3,
            clearcoat: 0.85,
            clearcoatRoughness: 0.06
        },
        'vivid-black': {
            color: new THREE.Color(0x000000),      // 纯黑
            metalness: 0.0,
            roughness: 0.2,
            clearcoat: 0.95,                        // 最强清漆 - 镜面黑效果
            clearcoatRoughness: 0.02
        },
        'vivid-black-flame': {
            color: new THREE.Color(0xffffff),      // 白色底色，以免遮挡纹理 (纹理本身包含黑色背景)
            metalness: 0.1,
            roughness: 0.2,
            clearcoat: 0.95,
            clearcoatRoughness: 0.02,
            texture: flameTexture,                  // 火焰纹理
            textureAlpha: 0.8                       // 纹理混合强度
        },
        'year-of-horse': {
            color: new THREE.Color(0xE0E0E0),      // 银白色底色
            metalness: 0.1,
            roughness: 0.3,
            clearcoat: 0.8,
            clearcoatRoughness: 0.1,
            texture: horseTexture,                  // 马年纹理
            textureAlpha: 0.9
        },
        'white-onyx-pearl': {
            color: new THREE.Color(0xE8E4E0),      // 米白/暖白珍珠
            metalness: 0.05,
            roughness: 0.2,
            clearcoat: 0.9,                         // 珍珠般柔和反光
            clearcoatRoughness: 0.05
        },
        'brilliant-red': {
            color: new THREE.Color(0x8B0000),      // 深酒红/栗红色
            metalness: 0.15,
            roughness: 0.25,
            clearcoat: 0.9,                         // 高光泽深红
            clearcoatRoughness: 0.05
        },
        'olive-steel': {
            color: new THREE.Color(0x556B2F),      // 橄榄钢金属色/亮黑
            metalness: 0.7,                         // 强金属感
            roughness: 0.4,
            clearcoat: 0.6,
            clearcoatRoughness: 0.15
        },
        'custom-pattern': {
            color: new THREE.Color(0xffffff),      // 白色底色，让纹理颜色更准确
            metalness: 0.1,
            roughness: 0.25,
            clearcoat: 0.85,
            clearcoatRoughness: 0.05,
            texture: customTexture,                 // 用户上传的自定义纹理
            textureAlpha: 1.0
        },
        'custom-color': {
            color: new THREE.Color(customColorHex), // 用户自定义颜色
            metalness: 0.2,
            roughness: 0.25,
            clearcoat: 0.9,
            clearcoatRoughness: 0.05
        }
    };

    const paintConfig = carPaintConfig[color] || carPaintConfig['dark-billiard-gray'];

    // 首次加载时分析模型结构并识别可喷漆部件
    useEffect(() => {
        if (scene) {
            const allNodes = [];
            const meshMap = {};
            const paintableMeshes = [];

            // 计算整个场景的边界框
            const sceneBox = new THREE.Box3().setFromObject(scene);
            const sceneSize = new THREE.Vector3();
            sceneBox.getSize(sceneSize);
            const sceneCenter = new THREE.Vector3();
            sceneBox.getCenter(sceneCenter);

            console.log('=== MODEL DEBUG INFO ===');
            console.log('Box Min:', sceneBox.min);
            console.log('Box Max:', sceneBox.max);
            console.log('Size:', sceneSize);
            console.log('Center:', sceneCenter);
            console.log('========================');

            // 定义明确的包含和排除规则
            // 包含：油箱、挡泥板、侧板、整流罩
            const INCLUDED_PATTERNS = [
                'fuelTank', 'FuelTank', 'tank', 'Tank',
                'wingFront', 'WingFront', 'fenderFront', 'FenderFront',
                'wingRear', 'WingRear', 'fenderRear', 'FenderRear',
                'sidePanel', 'SidePanel', 'cover', 'Cover',
                'fairing', 'Fairing'
            ];

            // 排除：发动机、轮毂、轮胎、刹车、前叉、车把、车架结构、螺丝、线缆
            const EXCLUDED_PATTERNS = [
                'engine', 'Engine', 'motor', 'Motor', 'cylinder', 'Cylinder', 'crank', 'Crank',
                'wheel', 'Wheel', 'rim', 'Rim', 'tire', 'Tire', 'tyre', 'Tyre', 'hub', 'Hub', 'spoke', 'Spoke',
                'brake', 'Brake', 'caliper', 'Caliper', 'disc', 'Disc', 'rotor', 'Rotor',
                'fork', 'Fork', 'suspension', 'Suspension', 'shock', 'Shock', 'absorber', 'Absorber',
                'handle', 'Handle', 'bar', 'Bar', 'grip', 'Grip', 'lever', 'Lever', 'mirror', 'Mirror',
                'frame', 'Frame', 'chassis', 'Chassis', 'stand', 'Stand',
                'screw', 'Screw', 'bolt', 'Bolt', 'nut', 'Nut', 'washer', 'Washer',
                'cable', 'Cable', 'wire', 'Wire', 'hose', 'Hose', 'line', 'Line',
                'light', 'Light', 'lamp', 'Lamp', 'indicator', 'Indicator', 'lens', 'Lens', 'glass', 'Glass',
                'seat', 'Seat', 'saddle', 'Saddle',
                'exhaust', 'Exhaust', 'pipe', 'Pipe', 'muffler', 'Muffler',
                'foot', 'Foot', 'peg', 'Peg', 'pedal', 'Pedal',
                'chain', 'Chain', 'belt', 'Belt', 'drive', 'Drive',
                'radiator', 'Radiator', 'cooler', 'Cooler',
                'battery', 'Battery', 'box', 'Box',
                'logo', 'Logo', 'badge', 'Badge', 'emblem', 'Emblem',
                'dash', 'Dash', 'gauge', 'Gauge', 'instrument', 'Instrument',
                'plate', 'Plate', 'license', 'License'
            ];

            // 特殊排除的具体部件名称 (根据用户反馈)
            const SPECIFIC_EXCLUDED_PARTS = [
                'Object_186', // 挡风玻璃
                'Object_208', // 地板
                'Object_',    // 很多未命名的通用部件通常不是车身板件
                // 以下部件不应出现在可喷漆列表中
                'oilFilter_0',
                'fuelPump_0',
                'fuelTankAirboxNet_0',
                'fuelTankAirboxNet_0_1',
                'tachometer_0',
                'tachometer_1',
                'tachometer_2',
                'tachometer_3',
                'tachometer_6',
                'reflectorsRear_0',
                'reflectorsRear_1',
                'sparkPlugs_1',
                'voltageRegulator_0',
                'voltageRegulator_1',
                'oilTank_0',
                'oilTank_1',
                'injector_0',
            ];

            scene.traverse((child) => {
                if (child.name && child.isMesh) {
                    // 保存mesh引用
                    meshMap[child.name] = child;

                    // 备份原始材质
                    if (!originalMaterialsRef.current[child.name]) {
                        originalMaterialsRef.current[child.name] = child.material.clone();
                    }

                    // 计算每个mesh的边界框
                    const box = new THREE.Box3().setFromObject(child);
                    const center = new THREE.Vector3();
                    const size = new THREE.Vector3();
                    box.getCenter(center);
                    box.getSize(size);

                    const relativeSize = size.length() / sceneSize.length();

                    // 材质分析
                    const mat = child.material;
                    const matColor = mat.color ? mat.color.getHexString() : '';

                    // 1. 检查是否在排除列表中
                    const isExcludedByName = EXCLUDED_PATTERNS.some(pattern => child.name.includes(pattern));

                    // 2. 检查是否在包含列表中
                    const isIncludedByName = INCLUDED_PATTERNS.some(pattern => child.name.includes(pattern));

                    // 3. 材质检查：排除明显的 Chrome、橡胶、透明件
                    const isChrome = matColor === 'c0c0c0' || matColor === 'ffffff' || (mat.metalness > 0.8 && mat.roughness < 0.2);
                    const isRubber = matColor === '1a1a1a' || (mat.roughness > 0.8 && mat.metalness < 0.1);
                    const isGlass = mat.opacity < 0.9 || mat.transparent;

                    // 4. 尺寸检查：排除太小的部件 (螺丝等)
                    const isTiny = relativeSize < 0.02;

                    // 隐藏模型自带的地板
                    if (child.name === 'Object_208') {
                        child.visible = false;
                    }

                    // 启用阴影投射
                    child.castShadow = true;
                    child.receiveShadow = true;

                    // === 核心判断逻辑 ===
                    let isPaintable = false;

                    if (isIncludedByName) {
                        // 如果名字明确包含 (如 fuelTank)，则大概率是，除非材质非常不对 (如玻璃)
                        isPaintable = !isGlass;
                    } else if (isExcludedByName) {
                        // 如果名字明确排除 (如 engine)，则绝对不是
                        isPaintable = false;
                    } else {
                        // 名字不明确，依靠材质和尺寸兜底
                        // 必须是大部件，且不是金属/橡胶/玻璃
                        isPaintable = !isTiny && !isChrome && !isRubber && !isGlass && relativeSize > 0.05;
                    }

                    // 最后的防线：特定排除列表
                    if (SPECIFIC_EXCLUDED_PARTS.includes(child.name)) {
                        isPaintable = false;
                    }

                    // 针对 "Object_" 开头的未命名部件，如果没有明确包含规则，默认排除
                    if (child.name.startsWith('Object_') && !isIncludedByName) {
                        isPaintable = false;
                    }

                    allNodes.push({
                        name: child.name,
                        type: child.type,
                        isMesh: child.isMesh,
                        worldY: center.y,
                        size: relativeSize,
                        sizeLabel: relativeSize > 0.1 ? '🔴大' : relativeSize > 0.03 ? '🟡中' : '🟢小',
                        isPaintable: isPaintable,
                        materialColor: matColor
                    });

                    if (isPaintable) {
                        paintableMeshes.push(child.name);
                    }
                }
            });

            allMeshesRef.current = meshMap;
            paintableMeshesRef.current = paintableMeshes;

            // 按大小排序
            allNodes.sort((a, b) => b.size - a.size);

            if (onPaintableMeshes) {
                onPaintableMeshes(paintableMeshes);
            }

            console.log('📋 Total mesh nodes:', allNodes.length);
            console.log('🎨 Paintable meshes:', paintableMeshes.length, paintableMeshes);
        }
    }, [scene, onPaintableMeshes]);

    // 根据 hiddenNodes 控制显示/隐藏
    useEffect(() => {
        Object.entries(allMeshesRef.current).forEach(([name, mesh]) => {
            mesh.visible = !hiddenNodes.includes(name);
        });
    }, [hiddenNodes]);

    // 颜色更新 - 应用PBR汽车漆材质到所有可喷漆部件，或恢复原始材质
    useEffect(() => {
        if (scene && paintableMeshesRef.current.length > 0) {
            paintableMeshesRef.current.forEach(meshName => {
                const mesh = allMeshesRef.current[meshName];
                if (mesh) {
                    if (color === 'original') {
                        // 恢复原始材质
                        const originalMat = originalMaterialsRef.current[meshName];
                        if (originalMat) {
                            mesh.material = originalMat.clone();
                            mesh.material.needsUpdate = true;
                        }
                    } else if (color === 'custom-pattern') {
                        // 自定义图案模式：只对选中的部件应用纹理
                        const isSelected = selectedPaintParts.includes(meshName);

                        if (isSelected && customTexture) {
                            // 选中的部件应用自定义纹理，并应用亮度调整
                            const brightnessColor = paintConfig.color.clone();
                            // 通过调整颜色的RGB值来实现亮度效果
                            brightnessColor.multiplyScalar(textureBrightness);

                            const materialConfig = {
                                color: brightnessColor,
                                metalness: paintConfig.metalness,
                                roughness: paintConfig.roughness,
                                clearcoat: paintConfig.clearcoat,
                                clearcoatRoughness: paintConfig.clearcoatRoughness,
                                envMapIntensity: 1.0 * textureBrightness, // 环境光也随亮度调整
                                reflectivity: 0.5,
                                map: customTexture
                            };
                            const carPaintMaterial = new THREE.MeshPhysicalMaterial(materialConfig);
                            mesh.material = carPaintMaterial;
                        } else {
                            // 未选中的部件恢复原始材质
                            const originalMat = originalMaterialsRef.current[meshName];
                            if (originalMat) {
                                mesh.material = originalMat.clone();
                                mesh.material.needsUpdate = true;
                            }
                        }
                    } else {
                        // 标准颜色模式：应用到所有可喷漆部件
                        const materialConfig = {
                            color: paintConfig.color.clone(),
                            metalness: paintConfig.metalness,
                            roughness: paintConfig.roughness,
                            clearcoat: paintConfig.clearcoat,
                            clearcoatRoughness: paintConfig.clearcoatRoughness,
                            envMapIntensity: 1.0,
                            reflectivity: 0.5
                        };

                        // 如果有纹理（如火焰、马年图案），应用纹理
                        if (paintConfig.texture) {
                            materialConfig.map = paintConfig.texture;
                        }

                        const carPaintMaterial = new THREE.MeshPhysicalMaterial(materialConfig);
                        mesh.material = carPaintMaterial;
                    }
                }
            });
            console.log('🎨 Color applied:', color === 'original' ? 'Original materials restored' : `PBR Car Paint: ${color}`,
                color === 'custom-pattern' ? `(${selectedPaintParts.length} parts selected)` : '',
                color === 'custom-color' ? `(${customColorHex})` : '');
        }
    }, [scene, color, paintConfig, customTexture, selectedPaintParts, customColorHex, textureBrightness]);

    return (
        <group ref={groupRef} dispose={null}>
            <Center top>
                <primitive
                    object={scene}
                    scale={debugScale}
                    position={[0, debugY, 0]}
                    rotation={[0, Math.PI / 2, 0]}
                />
            </Center>

            {/* ========== 可切换配件 (暂时隐藏，以免位置不对) ========== */}
            {/*
            {showEngineGuard && (
                <group>
                    <mesh position={[0.3, 0.5, 0.5]}>
                        <torusGeometry args={[0.3, 0.02, 8, 16, Math.PI]} />
                        <meshStandardMaterial color="#c0c0c0" metalness={1} roughness={0.1} />
                    </mesh>
                    <mesh position={[0.3, 0.5, -0.5]}>
                        <torusGeometry args={[0.3, 0.02, 8, 16, Math.PI]} />
                        <meshStandardMaterial color="#c0c0c0" metalness={1} roughness={0.1} />
                    </mesh>
                </group>
            )}

            {showWindshield && (
                <mesh position={[0.9, 1.3, 0]}>
                    <planeGeometry args={[0.5, 0.6]} />
                    <meshPhysicalMaterial
                        color="#88ccff"
                        transparent
                        opacity={0.3}
                        roughness={0}
                        metalness={0}
                        clearcoat={1}
                    />
                </mesh>
            )}

            {showSaddlebags && (
                <group>
                    <mesh position={[-0.8, 0.5, 0.6]}>
                        <boxGeometry args={[0.4, 0.3, 0.25]} />
                        <meshStandardMaterial color="#222" metalness={0.3} roughness={0.8} />
                    </mesh>
                    <mesh position={[-0.8, 0.5, -0.6]}>
                        <boxGeometry args={[0.4, 0.3, 0.25]} />
                        <meshStandardMaterial color="#222" metalness={0.3} roughness={0.8} />
                    </mesh>
                </group>
            )}
            */}
        </group>
    );
}

// ============================================
// 加载指示器
// ============================================
function Loader({ message }) {
    return (
        <Html center>
            <div className={styles.loader}>
                <div className={styles.spinner}></div>
                <p>{message || 'Loading 3D Model...'}</p>
            </div>
        </Html>
    );
}

// ============================================
// 相机控制组件 - 正侧面视角
// ============================================
function CameraController({ height, distance, lookAtY = 0.8 }) {
    const { camera } = useThree();
    useEffect(() => {
        // 从正侧面观看 (x=0, z=distance)，类似参考图的角度
        camera.position.set(0, height, distance);
        camera.lookAt(0, lookAtY, 0);  // 看向车身中心偏上，地面占屏幕1/4
        camera.updateProjectionMatrix();
    }, [camera, height, distance, lookAtY]);
    return null;
}

// 注意: 颜色和配件数据现在从 parts.js 导入

// ============================================
// 主 POC 页面
// ============================================
export default function POCPage() {
    const location = useLocation();
    const { t } = useLanguage();

    // 配置器状态 - 与ConfiguratorPage一致
    const [activeCategory, setActiveCategory] = useState('body');
    const [config, setConfig] = useState({
        trim: 'chrome',
        body: 'dark_billiard_gray',
        comfort: [],
        performance: [],
        style: []
    });

    // 颜色状态 - 默认显示原始颜色
    const [selectedColor, setSelectedColor] = useState('original');

    // 记录上次应用的配置key，用于检测新配置（即使是同一个页面的导航）
    const [lastAppliedConfigKey, setLastAppliedConfigKey] = useState(null);

    // 自定义颜色调色板状态
    const [customColorHex, setCustomColorHex] = useState('#FF6B00'); // 默认橙色
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showConfigLibrary, setShowConfigLibrary] = useState(false); // 配置库弹窗
    const [colorPickerHue, setColorPickerHue] = useState(25); // 色相 0-360
    const [colorPickerSaturation, setColorPickerSaturation] = useState(100); // 饱和度 0-100
    const [colorPickerLightness, setColorPickerLightness] = useState(50); // 亮度 0-100

    // HSL 转 Hex
    const hslToHex = (h, s, l) => {
        s /= 100;
        l /= 100;
        const a = s * Math.min(l, 1 - l);
        const f = n => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return Math.round(255 * color).toString(16).padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    };

    // 当HSL值变化时更新Hex颜色
    useEffect(() => {
        const newHex = hslToHex(colorPickerHue, colorPickerSaturation, colorPickerLightness);
        setCustomColorHex(newHex);
    }, [colorPickerHue, colorPickerSaturation, colorPickerLightness]);

    // 从ConfigDetailPage接收预设配置并应用
    useEffect(() => {
        // 使用 location.key 检测是否是新的导航（即使是同一个页面）
        const currentConfigKey = location.key;
        if (location.state?.presetConfig && currentConfigKey !== lastAppliedConfigKey) {
            const presetConfig = location.state.presetConfig;
            console.log('📦 Applying preset config from ConfigDetailPage:', presetConfig);
            setLastAppliedConfigKey(currentConfigKey);

            // 应用基础配置
            const newConfig = {
                trim: presetConfig.trim || 'chrome',
                body: presetConfig.body || 'dark_billiard_gray',
                comfort: Array.isArray(presetConfig.comfort) ? presetConfig.comfort : [],
                performance: Array.isArray(presetConfig.performance) ? presetConfig.performance : [],
                style: Array.isArray(presetConfig.style) ? presetConfig.style : []
            };
            setConfig(newConfig);

            // 应用颜色设置
            if (presetConfig.customization) {
                const customization = presetConfig.customization;

                // 应用选中的颜色模式
                if (customization.selectedColor) {
                    setSelectedColor(customization.selectedColor);
                }

                // 应用自定义颜色HSL值
                if (customization.selectedColor === 'custom-color' && customization.customColorHSL) {
                    setColorPickerHue(customization.customColorHSL.hue);
                    setColorPickerSaturation(customization.customColorHSL.saturation);
                    setColorPickerLightness(customization.customColorHSL.lightness);
                }

                // 如果有自定义纹理URL，加载纹理
                if (customization.selectedColor === 'custom-pattern' && customization.customTextureUrl) {
                    // 保存已上传的纹理URL，以便后续保存时使用
                    setUploadedTextureUrl(customization.customTextureUrl);

                    // 使用代理URL来绕过CORS限制
                    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
                    const proxyUrl = `${API_BASE_URL}/proxy/texture?url=${encodeURIComponent(customization.customTextureUrl)}`;

                    console.log('🎨 Loading custom texture via proxy:', proxyUrl);
                    const loader = new THREE.TextureLoader();
                    loader.load(
                        proxyUrl,
                        (texture) => {
                            console.log('✅ Custom texture loaded successfully');
                            texture.wrapS = THREE.RepeatWrapping;
                            texture.wrapT = THREE.RepeatWrapping;
                            texture.repeat.set(1, 1);
                            texture.center.set(0.5, 0.5);
                            setCustomTexture(texture);
                        },
                        (progress) => {
                            if (progress.total > 0) {
                                console.log('📥 Loading texture...', Math.round((progress.loaded / progress.total) * 100) + '%');
                            }
                        },
                        (error) => {
                            console.error('❌ Failed to load custom texture:', error);
                        }
                    );
                }

                // 应用选中的喷漆部件
                if (customization.selectedPaintParts && customization.selectedPaintParts.length > 0) {
                    setSelectedPaintParts(customization.selectedPaintParts);
                }
            } else {
                // 没有自定义设置时，根据body颜色设置3D模型颜色
                const colorMapping = {
                    'dark_billiard_gray': 'dark-billiard-gray',
                    'vivid_black': 'vivid-black',
                    'white_onyx_pearl': 'white-onyx-pearl',
                    'brilliant_red': 'brilliant-red',
                    'olive_steel_metallic_vivid_black': 'olive-steel'
                };
                if (colorMapping[presetConfig.body]) {
                    setSelectedColor(colorMapping[presetConfig.body]);
                }
            }

            // 显示提示
            if (location.state.configTitle) {
                console.log(`✅ Successfully loaded config: ${location.state.configTitle}`);
            }
        }
    }, [location.state, location.key, lastAppliedConfigKey]);

    // 自定义纹理状态
    const [customTexture, setCustomTexture] = useState(null);
    const [customTextureFile, setCustomTextureFile] = useState(null); // 原始文件用于上传
    const [uploadedTextureUrl, setUploadedTextureUrl] = useState(null); // 已上传的纹理URL（用于保存配置）
    const [textureScale, setTextureScale] = useState(1); // 纹理缩放 (0.5-3, 值越大图案越小)
    const [textureBrightness, setTextureBrightness] = useState(1); // 纹理亮度 (0.3-1.5)
    const fileInputRef = useRef(null);

    // Canvas引用 - 用于截图
    const canvasRef = useRef(null);

    // 排气声音播放状态
    const [playingSound, setPlayingSound] = useState(null);
    const audioRef = useRef(null);

    // 隐藏的节点列表（点击节点名称切换显示/隐藏）
    const [hiddenNodes] = useState([]);

    // 保存配置相关状态
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [savedConfigId, setSavedConfigId] = useState(null);
    const [savedConfigTitle, setSavedConfigTitle] = useState('');

    // 可喷漆部件列表
    const [paintableMeshes, setPaintableMeshes] = useState([]);

    // 选中的要渲染自定义图案的部件（默认只选中5个主要部件）
    const DEFAULT_PAINT_PARTS = [
        'fuelTankAirbox_0',
        'fuelTankAirbox_1',
        'wingFront_0',
        'fuelTank_0',
        'fuelTank_1'
    ];
    const [selectedPaintParts, setSelectedPaintParts] = useState([]);

    // 当可喷漆部件列表更新时，默认选中预设的5个部件
    useEffect(() => {
        if (paintableMeshes.length > 0 && selectedPaintParts.length === 0) {
            // 只选中存在于paintableMeshes中的默认部件
            const defaultSelected = DEFAULT_PAINT_PARTS.filter(part => paintableMeshes.includes(part));
            setSelectedPaintParts(defaultSelected);
        }
    }, [paintableMeshes]);

    // 当纹理缩放变化时，更新纹理的repeat属性
    useEffect(() => {
        if (customTexture) {
            customTexture.repeat.set(textureScale, textureScale);
            customTexture.needsUpdate = true;
        }
    }, [customTexture, textureScale]);

    // Debug State - 3D模型显示参数
    const [debugScale] = useState(2.0);    // 默认缩放
    const [debugY] = useState(-2.0);           // 默认Y位置
    const [camHeight] = useState(1.2);      // 相机高度
    const [camDist] = useState(3.6);          // 足够远以展示完整车身
    const [camLookAtY] = useState(1.4);    // 观看目标Y位置，控制地面在屏幕中的占比

    // 声音引擎（保留用于排气声音播放）
    const { } = useSoundEngine();

    // 获取分类名称（使用国际化）
    const getCategoryName = (categoryId) => {
        return t(`categories.${categoryId}`) || categoryId.toUpperCase();
    };

    // 获取配件显示名称（使用国际化）
    const getPartName = (partId, category) => {
        // 尝试从 parts.{category}.{partId} 获取翻译
        const translationKey = `parts.${category}.${partId}`;
        const translated = t(translationKey);
        // 如果找到翻译则返回，否则返回原始partId
        return translated !== translationKey ? translated : partId;
    };

    // 处理自定义图案上传
    const handleCustomTextureUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            alert(t('poc.alerts.uploadImageOnly'));
            return;
        }

        // 验证文件大小 (最大 10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert(t('poc.alerts.imageTooLarge'));
            return;
        }

        // 保存原始文件用于后续上传
        setCustomTextureFile(file);
        setUploadedTextureUrl(null); // 清除旧的URL，因为有新文件需要上传

        // 创建预览URL
        const previewUrl = URL.createObjectURL(file);

        // 创建 Three.js 纹理
        const loader = new THREE.TextureLoader();
        loader.load(previewUrl, (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(1, 1);
            texture.center.set(0.5, 0.5);
            setCustomTexture(texture);
            // 自动选择自定义颜色
            setSelectedColor('custom-pattern');
        });
    };


    // 播放排气声音
    const handlePlaySound = (e, soundUrl, partId) => {
        e.stopPropagation(); // 防止触发卡片选择

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        if (playingSound === partId) {
            // 如果正在播放同一个，则停止
            setPlayingSound(null);
            return;
        }

        // 播放新的声音
        const audio = new Audio(soundUrl);
        audioRef.current = audio;
        setPlayingSound(partId);

        audio.play().catch(err => {
            console.error('Failed to play sound:', err);
            setPlayingSound(null);
        });

        audio.onended = () => {
            setPlayingSound(null);
            audioRef.current = null;
        };
    };

    // 处理配件选择
    const handlePartSelect = (partId) => {
        const category = activeCategory;
        const multi = isMultiSelect(category);

        setConfig(prev => {
            if (multi) {
                // 多选模式 (comfort, performance, style)
                const currentSelection = prev[category] || [];
                if (currentSelection.includes(partId)) {
                    return { ...prev, [category]: currentSelection.filter(id => id !== partId) };
                } else {
                    return { ...prev, [category]: [...currentSelection, partId] };
                }
            } else {
                // 单选模式 (trim, body)
                return { ...prev, [category]: partId };
            }
        });

        // 如果选择了body颜色，同步更新3D模型颜色
        if (category === 'body') {
            const colorMapping = {
                'dark_billiard_gray': 'dark-billiard-gray',
                'vivid_black': 'vivid-black',
                'white_onyx_pearl': 'white-onyx-pearl',
                'brilliant_red': 'brilliant-red',
                'olive_steel_metallic_vivid_black': 'olive-steel'
            };
            if (colorMapping[partId]) {
                setSelectedColor(colorMapping[partId]);
            }
        }
    };

    // 应用配置库中的配置
    const handleApplyLibraryConfig = (configSummary) => {
        if (!configSummary) return;

        console.log('📦 应用配置:', configSummary);

        // 应用配置到当前状态
        const newConfig = { ...config };

        // 检查是否有自定义配置 (customization) - 新格式
        const customization = configSummary.customization;

        // 颜色代码映射（旧格式 color 字段使用的是 m04, m07 等代码）
        const oldColorCodeMapping = {
            'm04': 'vivid-black',
            'm07': 'white-onyx-pearl',
            'm44': 'brilliant-red',
            'm78': 'olive-steel',
            'm85': 'dark-billiard-gray'
        };

        // 新格式 body 字段映射
        const bodyColorMapping = {
            'dark_billiard_gray': 'dark-billiard-gray',
            'vivid_black': 'vivid-black',
            'white_onyx_pearl': 'white-onyx-pearl',
            'brilliant_red': 'brilliant-red',
            'olive_steel_metallic_vivid_black': 'olive-steel'
        };

        if (customization) {
            // 新格式：应用自定义颜色/纹理配置
            if (customization.selectedColor) {
                setSelectedColor(customization.selectedColor);

                // 如果是自定义颜色
                if (customization.selectedColor === 'custom-color' && customization.customColorHex) {
                    setCustomColorHex(customization.customColorHex);
                    // 恢复 HSL 值
                    if (customization.customColorHSL) {
                        setColorPickerHue(customization.customColorHSL.hue || 30);
                        setColorPickerSaturation(customization.customColorHSL.saturation || 100);
                        setColorPickerLightness(customization.customColorHSL.lightness || 50);
                    }
                }

                // 如果是自定义纹理
                if (customization.selectedColor === 'custom-pattern' && customization.customTextureUrl) {
                    // 加载纹理
                    const textureLoader = new THREE.TextureLoader();
                    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
                    const proxyUrl = `${API_BASE_URL}/proxy/texture?url=${encodeURIComponent(customization.customTextureUrl)}`;

                    textureLoader.load(
                        proxyUrl,
                        (texture) => {
                            texture.wrapS = THREE.RepeatWrapping;
                            texture.wrapT = THREE.RepeatWrapping;
                            texture.repeat.set(textureScale, textureScale);
                            setCustomTexture(texture);
                            setUploadedTextureUrl(customization.customTextureUrl);
                            console.log('✅ 纹理加载成功');
                        },
                        undefined,
                        (error) => {
                            console.error('❌ 纹理加载失败:', error);
                        }
                    );

                    // 恢复选中的喷漆部件
                    if (customization.selectedPaintParts && customization.selectedPaintParts.length > 0) {
                        setSelectedPaintParts(customization.selectedPaintParts);
                    }
                }
            }
        } else if (configSummary.color) {
            // 旧格式：使用 color 字段 (m04, m07 等)
            console.log('🎨 应用旧格式颜色:', configSummary.color);
            if (oldColorCodeMapping[configSummary.color]) {
                setSelectedColor(oldColorCodeMapping[configSummary.color]);
            }
        } else if (configSummary.body) {
            // 新格式但没有 customization：使用 body 颜色映射
            if (bodyColorMapping[configSummary.body]) {
                setSelectedColor(bodyColorMapping[configSummary.body]);
            }
        }

        // 应用body颜色（无论是否有customization都更新config.body）
        if (configSummary.body) {
            newConfig.body = configSummary.body;
        }

        // 应用trim
        if (configSummary.trim) {
            newConfig.trim = configSummary.trim;
        }

        // 应用comfort (多选)
        if (configSummary.comfort) {
            newConfig.comfort = Array.isArray(configSummary.comfort)
                ? configSummary.comfort
                : [configSummary.comfort];
        }

        // 应用performance
        if (configSummary.performance) {
            newConfig.performance = Array.isArray(configSummary.performance)
                ? configSummary.performance
                : (configSummary.performance === 'none' ? [] : [configSummary.performance]);
        }

        // 应用style (多选)
        if (configSummary.style) {
            newConfig.style = Array.isArray(configSummary.style)
                ? configSummary.style
                : [configSummary.style];
        }

        setConfig(newConfig);
        console.log('✅ 配置已应用');
    };

    // 根据配置获取默认预览图片URL
    const getDefaultPreviewImageUrl = () => {
        // body 颜色到 360 图片颜色代码的映射
        const colorCodeMapping = {
            'dark_billiard_gray': 'm85',
            'vivid_black': 'm04',
            'white_onyx_pearl': 'm07',
            'brilliant_red': 'm44',
            'olive_steel_metallic_vivid_black': 'm78'
        };

        // 获取颜色代码，默认使用 m85 (Gunship Gray)
        const colorCode = colorCodeMapping[config.body] || 'm85';

        // 返回对应颜色的 360 图片（第1帧，侧面视角）
        return `/assets/360/2026-fat-boy-${colorCode}-motorcycle/2026-fat-boy-${colorCode}-motorcycle-01.jpg`;
    };

    // 从Canvas截取3D模型图片
    const captureCanvasScreenshot = async () => {
        try {
            console.log('🔍 canvasRef.current:', canvasRef.current);
            if (!canvasRef.current) {
                console.warn('❌ Canvas ref not available');
                return null;
            }

            // 获取Canvas DOM元素
            const canvas = canvasRef.current.querySelector ?
                canvasRef.current.querySelector('canvas') :
                canvasRef.current;

            console.log('🔍 Found canvas element:', canvas);
            if (!canvas || !canvas.toDataURL) {
                console.warn('❌ Canvas element not found or toDataURL not available');
                return null;
            }

            // 导出为PNG图片 (可能因为跨域纹理而失败)
            let dataUrl;
            try {
                console.log('📷 Attempting canvas.toDataURL...');
                dataUrl = canvas.toDataURL('image/png', 1.0);
                console.log('✅ Canvas toDataURL successful, length:', dataUrl?.length);
            } catch (taintError) {
                // Canvas被跨域资源污染，无法导出
                console.warn('⚠️ Canvas tainted by cross-origin data:', taintError.message);

                // 使用备用方案：获取当前颜色对应的默认图片
                const fallbackUrl = getDefaultPreviewImageUrl();
                console.log('📷 Using fallback image:', fallbackUrl);

                try {
                    const fallbackResponse = await fetch(fallbackUrl);
                    if (fallbackResponse.ok) {
                        const blob = await fallbackResponse.blob();
                        console.log('✅ Fallback image fetched, size:', blob.size);
                        return new File([blob], 'config-preview.jpg', { type: 'image/jpeg' });
                    }
                } catch (fallbackErr) {
                    console.error('❌ Fallback image fetch failed:', fallbackErr);
                }
                return null;
            }

            // 将dataUrl转换为Blob
            const response = await fetch(dataUrl);
            const blob = await response.blob();
            console.log('✅ Screenshot blob created, size:', blob.size);

            // 创建File对象
            const file = new File([blob], 'config-preview.png', { type: 'image/png' });
            console.log('✅ Screenshot file created:', file.name, file.size);
            return file;
        } catch (err) {
            console.error('❌ Failed to capture canvas:', err);
            return null;
        }
    };

    // 保存配置到数据库
    const handleSaveConfiguration = async () => {
        setIsSaving(true);

        try {
            // 1. 首先截取3D模型截图
            console.log('📸 Capturing 3D model screenshot...');
            const screenshotFile = await captureCanvasScreenshot();

            // 2. 如果有自定义纹理，先上传到服务器（或使用已上传的URL）
            let textureUrlToSave = uploadedTextureUrl; // 优先使用已上传的URL
            if (selectedColor === 'custom-pattern' && customTextureFile) {
                // 如果有新的文件需要上传
                const textureResult = await api.uploadCustomTexture(customTextureFile);
                if (textureResult.success) {
                    textureUrlToSave = textureResult.data.url;
                    setUploadedTextureUrl(textureUrlToSave); // 保存URL以便后续使用
                    setCustomTextureFile(null); // 清除文件，避免重复上传
                } else {
                    console.error('Failed to upload texture:', textureResult.error);
                }
            }

            // 3. 构建完整的配置数据，包含自定义颜色和纹理信息
            const configData = {
                ...config,
                customization: {
                    selectedColor: selectedColor,
                    customColorHex: selectedColor === 'custom-color' ? customColorHex : null,
                    customColorHSL: selectedColor === 'custom-color' ? {
                        hue: colorPickerHue,
                        saturation: colorPickerSaturation,
                        lightness: colorPickerLightness
                    } : null,
                    hasCustomTexture: selectedColor === 'custom-pattern' && customTexture !== null,
                    customTextureUrl: textureUrlToSave,
                    selectedPaintParts: selectedColor === 'custom-pattern' ? selectedPaintParts : []
                }
            };

            // 4. 生成配置标题 - 根据颜色类型智能命名
            let colorDisplayName;
            if (selectedColor === 'custom-color') {
                // 自定义颜色 - 显示"自定义色"
                colorDisplayName = t('poc.customColor');
            } else if (selectedColor === 'custom-pattern') {
                // 自定义纹理 - 显示"自定义纹理"
                colorDisplayName = t('poc.uploadedTexture');
            } else {
                // 标准颜色 - 使用翻译后的颜色名称
                colorDisplayName = t(`parts.body.${selectedColor}`) || selectedColor;
            }

            const timestamp = new Date().toLocaleString('zh-CN', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            const title = `Fat Boy ${colorDisplayName} - ${timestamp}`;

            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
            const token = localStorage.getItem('accessToken');

            // 5. 创建配置
            const response = await fetch(`${API_BASE_URL}/configurations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    title,
                    description: `Custom Fat Boy configuration with ${colorDisplayName}`,
                    config_data: configData,
                    total_price: totalPrice
                })
            });

            const result = await response.json();

            if (result.success) {
                const configId = result.data.id;

                // 6. 上传3D模型截图作为封面图片
                if (screenshotFile) {
                    console.log('📤 Uploading screenshot as cover image...');
                    console.log('📤 Config ID:', configId);
                    console.log('📤 Screenshot file:', screenshotFile.name, screenshotFile.size, screenshotFile.type);
                    try {
                        const uploadResult = await api.uploadConfigurationImages(configId, [screenshotFile]);
                        console.log('📤 Upload result:', uploadResult);
                        if (uploadResult.success) {
                            console.log('✅ Screenshot uploaded successfully');
                        } else {
                            console.error('Failed to upload screenshot:', uploadResult.error, uploadResult.details);
                        }
                    } catch (uploadError) {
                        console.error('Screenshot upload error:', uploadError);
                        // 不阻止保存成功的流程
                    }
                }

                setSavedConfigId(configId);
                setSavedConfigTitle(title);
                setShowSaveSuccess(true);
            } else {
                if (response.status === 401) {
                    alert(t('poc.alerts.loginToSave'));
                } else {
                    alert(result.error || t('poc.alerts.saveFailed'));
                }
            }
        } catch (error) {
            console.error('Save configuration error:', error);
            // 显示更详细的错误信息帮助调试
            const errorMessage = error.message || t('poc.alerts.networkError');
            alert(`${t('poc.alerts.saveFailed')}: ${errorMessage}`);
        } finally {
            setIsSaving(false);
        }
    };

    // 提交配置到官方审核
    const handleSubmitForReview = async () => {
        if (!savedConfigId) return;

        try {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`${API_BASE_URL}/configurations/${savedConfigId}/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                }
            });

            const result = await response.json();

            if (result.success) {
                alert(t('poc.alerts.submitSuccess'));
                setShowSaveSuccess(false);
            } else {
                alert(result.error || t('poc.alerts.submitFailed'));
            }
        } catch (error) {
            console.error('Submit for review error:', error);
            alert(t('poc.alerts.networkError'));
        }
    };

    // 计算配置总价格
    const calculateTotalPrice = () => {
        let total = BASE_PRICE;

        Object.entries(config).forEach(([category, selection]) => {
            const categoryParts = PARTS[category];
            if (!categoryParts) return;

            if (Array.isArray(selection)) {
                // 多选类别
                selection.forEach(partId => {
                    const part = categoryParts.find(p => p.id === partId);
                    if (part) total += part.price;
                });
            } else {
                // 单选类别
                const part = categoryParts.find(p => p.id === selection);
                if (part) total += part.price;
            }
        });

        return total;
    };

    const totalPrice = calculateTotalPrice();

    // 渲染配件选择器
    const renderPartSelector = () => {
        const parts = PARTS[activeCategory];
        if (!parts) return null;

        const isMulti = isMultiSelect(activeCategory);
        const currentSelection = config[activeCategory];

        // body类别 - 颜色色块
        if (activeCategory === 'body') {
            return (
                <>
                    <div className={styles.bodyColorGrid}>
                        {parts.map(part => {
                            const isSelected = currentSelection === part.id;
                            return (
                                <button
                                    key={part.id}
                                    className={`${styles.bodyColorBtn} ${isSelected ? styles.selected : ''}`}
                                    onClick={() => handlePartSelect(part.id)}
                                >
                                    <span
                                        className={styles.bodyColorSwatch}
                                        style={{ backgroundColor: part.colorCode || '#888' }}
                                    />
                                    <span className={styles.bodyColorName}>{getPartName(part.id, 'body')}</span>
                                    {part.price > 0 && (
                                        <span className={styles.bodyColorPrice}>+${part.price.toLocaleString()}</span>
                                    )}
                                </button>
                            );
                        })}
                        {/* 自定义颜色选项 */}
                        <button
                            className={`${styles.bodyColorBtn} ${styles.customColorOption} ${selectedColor === 'custom-color' ? styles.selected : ''}`}
                            onClick={() => {
                                setSelectedColor('custom-color');
                                setShowColorPicker(true);
                            }}
                        >
                            <span
                                className={styles.bodyColorSwatch}
                                style={{
                                    background: `conic-gradient(from 0deg, red, yellow, lime, aqua, blue, magenta, red)`
                                }}
                            />
                            <span className={styles.bodyColorName}>{t('poc.customColor')}</span>
                        </button>
                        {/* 自定义纹理上传按钮 */}
                        <button
                            className={`${styles.bodyColorBtn} ${styles.customTextureBtn} ${selectedColor === 'custom-pattern' ? styles.selected : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span
                                className={styles.bodyColorSwatch}
                                style={{
                                    background: customTexture
                                        ? `url(${customTexture.image?.src || ''}) center/cover`
                                        : 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                                }}
                            >
                                {!customTexture && <span className={styles.uploadIcon}>📤</span>}
                            </span>
                            <span className={styles.bodyColorName}>
                                {customTexture ? t('poc.uploadedTexture') : t('poc.uploadTexture')}
                            </span>
                        </button>
                    </div>

                    {/* 纹理大小调整滑块 - 仅在有自定义纹理时显示 */}
                    {selectedColor === 'custom-pattern' && customTexture && (
                        <>
                            <div className={styles.textureScalePanel}>
                                <div className={styles.textureScaleHeader}>
                                    <span>{t('poc.textureScale.title')}</span>
                                    <span className={styles.textureScaleValue}>{textureScale.toFixed(1)}x</span>
                                </div>
                                <div className={styles.textureScaleControl}>
                                    <span className={styles.scaleLabel}>{t('poc.textureScale.large')}</span>
                                    <input
                                        type="range"
                                        min="0.3"
                                        max="3"
                                        step="0.1"
                                        value={textureScale}
                                        onChange={(e) => setTextureScale(parseFloat(e.target.value))}
                                        className={styles.textureScaleSlider}
                                    />
                                    <span className={styles.scaleLabel}>{t('poc.textureScale.small')}</span>
                                </div>
                                <div className={styles.textureScalePresets}>
                                    {[0.5, 1, 1.5, 2].map(scale => (
                                        <button
                                            key={scale}
                                            className={`${styles.scalePresetBtn} ${Math.abs(textureScale - scale) < 0.05 ? styles.active : ''}`}
                                            onClick={() => setTextureScale(scale)}
                                        >
                                            {scale}x
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* 纹理亮度调整滑块 */}
                            <div className={styles.textureScalePanel}>
                                <div className={styles.textureScaleHeader}>
                                    <span>{t('poc.textureBrightness.title')}</span>
                                    <span className={styles.textureScaleValue}>{Math.round(textureBrightness * 100)}%</span>
                                </div>
                                <div className={styles.textureScaleControl}>
                                    <span className={styles.scaleLabel}>{t('poc.textureBrightness.dark')}</span>
                                    <input
                                        type="range"
                                        min="0.3"
                                        max="1.5"
                                        step="0.05"
                                        value={textureBrightness}
                                        onChange={(e) => setTextureBrightness(parseFloat(e.target.value))}
                                        className={styles.textureScaleSlider}
                                    />
                                    <span className={styles.scaleLabel}>{t('poc.textureBrightness.bright')}</span>
                                </div>
                                <div className={styles.textureScalePresets}>
                                    {[0.5, 0.75, 1, 1.25].map(brightness => (
                                        <button
                                            key={brightness}
                                            className={`${styles.scalePresetBtn} ${Math.abs(textureBrightness - brightness) < 0.03 ? styles.active : ''}`}
                                            onClick={() => setTextureBrightness(brightness)}
                                        >
                                            {Math.round(brightness * 100)}%
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </>
            );
        }

        // trim类别 - 简单选项
        if (activeCategory === 'trim') {
            return (
                <div className={styles.trimOptions}>
                    {parts.map(part => {
                        const isSelected = currentSelection === part.id;
                        return (
                            <button
                                key={part.id}
                                className={`${styles.trimBtn} ${isSelected ? styles.selected : ''}`}
                                onClick={() => handlePartSelect(part.id)}
                            >
                                <span className={styles.trimName}>{getPartName(part.id, 'trim')}</span>
                                {part.price > 0 && (
                                    <span className={styles.trimPrice}>+${part.price.toLocaleString()}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            );
        }

        // 套件类别 (comfort, performance, style) - 多选卡片
        // 分离官方配件和第三方配件
        const officialParts = parts.filter(p => !p.isThirdParty);
        const thirdPartyParts = parts.filter(p => p.isThirdParty);

        return (
            <div className={styles.packageSection}>
                <div className={styles.packageGrid}>
                    {officialParts.map(part => {
                        const isSelected = isMulti
                            ? (currentSelection || []).includes(part.id)
                            : currentSelection === part.id;
                        return (
                            <div
                                key={part.id}
                                className={`${styles.packageCard} ${isSelected ? styles.selected : ''}`}
                                onClick={() => handlePartSelect(part.id)}
                            >
                                <div className={styles.packageHeader}>
                                    <span className={styles.packageName}>{getPartName(part.id, activeCategory)}</span>
                                    <span className={styles.packagePrice}>${part.price.toLocaleString()}</span>
                                </div>
                                {part.itemCount && (
                                    <div className={styles.packageItems}>
                                        {part.itemCount} {t('parts.package_items')}
                                    </div>
                                )}
                                <div className={styles.packageCheckbox}>
                                    {isSelected ? '✓' : '+'}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 第三方合作品牌配件区域 */}
                {thirdPartyParts.length > 0 && (
                    <>
                        <div className={styles.sectionDivider}>
                            <span className={styles.partnerSectionTitle}>{t('poc.partnerBrands')}</span>
                        </div>
                        <div className={styles.packageGrid}>
                            {thirdPartyParts.map(part => {
                                const isSelected = isMulti
                                    ? (currentSelection || []).includes(part.id)
                                    : currentSelection === part.id;
                                return (
                                    <div
                                        key={part.id}
                                        className={`${styles.packageCard} ${styles.thirdPartyCard} ${isSelected ? styles.selected : ''}`}
                                        onClick={() => handlePartSelect(part.id)}
                                    >
                                        {/* 品牌标识 */}
                                        <div className={styles.brandHeader}>
                                            <span className={styles.brandBadge}>{t('poc.partner')}</span>
                                            <span className={styles.brandName}>{t('poc.byBrand')} {part.brand}</span>
                                        </div>

                                        {/* 产品图片 */}
                                        {part.image && (
                                            <div className={styles.productImageContainer}>
                                                <img
                                                    src={part.image}
                                                    alt={getPartName(part.id, activeCategory)}
                                                    className={styles.productImage}
                                                />
                                            </div>
                                        )}

                                        <div className={styles.packageHeader}>
                                            <span className={styles.packageName}>{getPartName(part.id, activeCategory)}</span>
                                            <span className={styles.packagePrice}>${part.price.toLocaleString()}</span>
                                        </div>

                                        {/* 排气声音播放按钮 */}
                                        {part.soundUrl && (
                                            <button
                                                className={`${styles.soundPlayBtn} ${playingSound === part.id ? styles.playing : ''}`}
                                                onClick={(e) => handlePlaySound(e, part.soundUrl, part.id)}
                                                title={t('poc.listenToExhaust')}
                                            >
                                                <span className={styles.soundIcon}>
                                                    {playingSound === part.id ? '⏹' : '🔊'}
                                                </span>
                                                <span className={styles.soundLabel}>
                                                    {playingSound === part.id ? t('poc.stopSound') : t('poc.listenSound')}
                                                </span>
                                            </button>
                                        )}

                                        {/* 产品特点 */}
                                        {part.features && part.features.length > 0 && (
                                            <div className={styles.featuresList}>
                                                {part.features.slice(0, 3).map((feature, idx) => (
                                                    <span key={idx} className={styles.featureItem}>✓ {feature}</span>
                                                ))}
                                            </div>
                                        )}

                                        <div className={styles.packageCheckbox}>
                                            {isSelected ? '✓' : '+'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={styles.configuratorContainer}>
            {/* 3D Viewer - 全宽 */}
            <div className={styles.viewerSection} ref={canvasRef}>
                <Canvas
                    gl={{ antialias: true, preserveDrawingBuffer: true }}
                    shadows
                >
                    {/* 纯白背景 */}
                    <color attach="background" args={['#ffffff']} />

                    <Suspense fallback={<Loader message={t('poc.loading')} />}>
                        <CameraController height={camHeight} distance={camDist} lookAtY={camLookAtY} />

                        {/* 工作室光照 + 环境反射（仅用于金属反射，不显示背景） */}
                        <ambientLight intensity={0.6} />
                        {/* 主光源 - 投射阴影 */}
                        <directionalLight
                            position={[5, 10, 5]}
                            intensity={1.5}
                            color="#ffffff"
                            castShadow
                            shadow-mapSize-width={2048}
                            shadow-mapSize-height={2048}
                            shadow-camera-far={50}
                            shadow-camera-left={-10}
                            shadow-camera-right={10}
                            shadow-camera-top={10}
                            shadow-camera-bottom={-10}
                            shadow-bias={-0.0001}
                        />
                        <directionalLight position={[-5, 8, -5]} intensity={0.5} color="#ffffff" />
                        <directionalLight position={[0, 5, 10]} intensity={0.6} color="#ffffff" />
                        <Environment preset="studio" background={false} />

                        <MotorcycleModel
                            color={selectedColor}
                            hiddenNodes={hiddenNodes}
                            debugScale={debugScale}
                            debugY={debugY}
                            onPaintableMeshes={setPaintableMeshes}
                            customTexture={customTexture}
                            selectedPaintParts={selectedPaintParts}
                            customColorHex={customColorHex}
                            textureBrightness={textureBrightness}
                        />

                        {/* 接触阴影 - 柔和的接触阴影 */}
                        <ContactShadows
                            position={[0, -0.001, 0]}
                            opacity={0.5}
                            scale={10}
                            blur={2}
                            far={5}
                            resolution={1024}
                            color="#000000"
                        />

                        {/* 白色地板 - 接收阴影 */}
                        <mesh
                            rotation={[-Math.PI / 2, 0, 0]}
                            position={[0, -0.002, 0]}
                            receiveShadow
                        >
                            <planeGeometry args={[50, 50]} />
                            <shadowMaterial opacity={0.3} />
                        </mesh>

                        <OrbitControls
                            enablePan={true}
                            target={[0, camLookAtY, 0]}
                            minDistance={2}
                            maxDistance={10}
                            maxPolarAngle={Math.PI / 2}
                        />
                    </Suspense>
                </Canvas>

                {/* 配置库入口按钮 - 3D模型右下角 */}
                <div className={styles.configLibraryEntry}>
                    <button
                        className={styles.configLibraryBtn}
                        onClick={() => setShowConfigLibrary(true)}
                        title={t('poc.configLibrary')}
                    >
                        <svg className={styles.configLibraryIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className={styles.configLibraryText}>{t('poc.configLibrary')}</span>
                    </button>
                </div>
            </div>

            {/* 分类标签导航 */}
            <div className={styles.categoryTabs}>
                {CATEGORIES.map(category => (
                    <button
                        key={category.id}
                        className={`${styles.categoryTab} ${activeCategory === category.id ? styles.active : ''}`}
                        onClick={() => setActiveCategory(category.id)}
                    >
                        {getCategoryName(category.id)}
                    </button>
                ))}
            </div>

            {/* 配件选择器区域 */}
            <div className={styles.selectorSection}>
                {renderPartSelector()}

                {/* 自定义颜色调色板 - 仅在body类别且选择custom-color时显示 */}
                {activeCategory === 'body' && showColorPicker && selectedColor === 'custom-color' && (
                    <div className={styles.colorPickerPanel}>
                        <div className={styles.colorPickerHeader}>
                            <span>🎨 {t('poc.colorPicker.title')}</span>
                            <div
                                className={styles.colorPickerPreview}
                                style={{ backgroundColor: customColorHex }}
                            />
                        </div>

                        {/* 色相滑块 */}
                        <div className={styles.colorPickerControl}>
                            <label>{t('poc.colorPicker.hue')}</label>
                            <div className={styles.hueSliderWrapper}>
                                <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={colorPickerHue}
                                    onChange={(e) => setColorPickerHue(parseInt(e.target.value))}
                                    className={styles.hueSlider}
                                />
                            </div>
                            <span>{colorPickerHue}°</span>
                        </div>

                        {/* 饱和度滑块 */}
                        <div className={styles.colorPickerControl}>
                            <label>{t('poc.colorPicker.saturation')}</label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={colorPickerSaturation}
                                onChange={(e) => setColorPickerSaturation(parseInt(e.target.value))}
                                className={styles.saturationSlider}
                                style={{
                                    background: `linear-gradient(to right, hsl(${colorPickerHue}, 0%, ${colorPickerLightness}%), hsl(${colorPickerHue}, 100%, ${colorPickerLightness}%))`
                                }}
                            />
                            <span>{colorPickerSaturation}%</span>
                        </div>

                        {/* 亮度滑块 */}
                        <div className={styles.colorPickerControl}>
                            <label>{t('poc.colorPicker.lightness')}</label>
                            <input
                                type="range"
                                min="10"
                                max="90"
                                value={colorPickerLightness}
                                onChange={(e) => setColorPickerLightness(parseInt(e.target.value))}
                                className={styles.lightnessSlider}
                                style={{
                                    background: `linear-gradient(to right, hsl(${colorPickerHue}, ${colorPickerSaturation}%, 10%), hsl(${colorPickerHue}, ${colorPickerSaturation}%, 50%), hsl(${colorPickerHue}, ${colorPickerSaturation}%, 90%))`
                                }}
                            />
                            <span>{colorPickerLightness}%</span>
                        </div>

                        {/* 颜色值显示 */}
                        <div className={styles.colorPickerValue}>
                            <span>HEX: {customColorHex.toUpperCase()}</span>
                        </div>

                        {/* 预设颜色快捷选择 */}
                        <div className={styles.presetColors}>
                            <span className={styles.presetLabel}>{t('poc.colorPicker.presets')}</span>
                            <div className={styles.presetColorGrid}>
                                {[
                                    { h: 0, s: 80, l: 50, nameKey: 'red' },
                                    { h: 25, s: 100, l: 50, nameKey: 'orange' },
                                    { h: 45, s: 100, l: 50, nameKey: 'gold' },
                                    { h: 120, s: 60, l: 40, nameKey: 'green' },
                                    { h: 200, s: 80, l: 50, nameKey: 'blue' },
                                    { h: 270, s: 70, l: 50, nameKey: 'purple' },
                                    { h: 330, s: 70, l: 50, nameKey: 'pink' },
                                    { h: 0, s: 0, l: 20, nameKey: 'darkGray' },
                                ].map((preset, i) => (
                                    <button
                                        key={i}
                                        className={styles.presetColorBtn}
                                        style={{ backgroundColor: `hsl(${preset.h}, ${preset.s}%, ${preset.l}%)` }}
                                        onClick={() => {
                                            setColorPickerHue(preset.h);
                                            setColorPickerSaturation(preset.s);
                                            setColorPickerLightness(preset.l);
                                        }}
                                        title={t(`poc.colorPicker.${preset.nameKey}`)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* 隐藏的文件上传输入 */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCustomTextureUpload}
                    style={{ display: 'none' }}
                />
            </div>

            {/* 底部固定价格栏 */}
            <div className={styles.bottomPriceBar}>
                <div className={styles.priceInfo}>
                    <span className={styles.priceLabel}>{t('poc.msrpConfigured')}</span>
                    <span className={styles.priceAmount}>${totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className={styles.bottomActions}>
                    <button
                        className={styles.saveBtn}
                        onClick={handleSaveConfiguration}
                        disabled={isSaving}
                    >
                        {isSaving ? t('poc.saving') : t('poc.save')}
                    </button>
                </div>
            </div>

            {/* 配置库弹窗 */}
            <ConfigLibraryModal
                isOpen={showConfigLibrary}
                onClose={() => setShowConfigLibrary(false)}
                onApplyConfig={handleApplyLibraryConfig}
            />

            {/* 保存成功弹窗 */}
            <SaveSuccessModal
                isOpen={showSaveSuccess}
                onClose={() => setShowSaveSuccess(false)}
                configId={savedConfigId}
                configTitle={savedConfigTitle}
                totalPrice={totalPrice}
                onSubmitForReview={handleSubmitForReview}
            />
        </div>
    );
}
